import React, { useMemo, useContext, useCallback } from 'react'
import { Grid, Hidden, SxProps, useMediaQuery, useTheme } from '@hermes/web-components'
import { HomePageContext } from '../../../../providers/HomePage'
import { newSearchTypesPractices } from '../../../../constants/global'
import SearchResultsList from './ResultsList'

import {
  SearchResultsModalRoot,
  SearchModalItemContainer,
  EmptySearchResultsContainer,
  MobileListContainer
} from './styles'
import NoResultsComponent from './NoResultsComponent'
import {
  alternativeTransformAutocompletePractices,
  transformAutocompleteKeyword,
  transformAutocompletePractices,
  transformAutocompleteSpecialist
} from '../../../../utils/transformAutocomplete'
import { AppDataContext } from '../../../../providers/AppData'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags'
import { AutocompleteSpecialty } from '../../../../providers/HomePage/types'

interface AutocompleteResultsModalProps {
  customStyle?: SxProps
  onClose: () => void
  setIsSearchFieldFocused: (isFocused: boolean) => void
  onChangeSearch: (...params: any) => void
  isOpen: boolean
  onInteract?: () => void
}

const AutocompleteResultsModal = ({
  onClose,
  customStyle,
  isOpen,
  onInteract,
  setIsSearchFieldFocused
}: AutocompleteResultsModalProps) => {
  const {
    changeSearch,
    changeFilter,
    openProfilePage,
    state: { filters, practicesAutocomplete, specialistsAutocomplete, specialties, conditionsProcedures }
  } = useContext(HomePageContext)
  const { tenantSettings } = useContext(AppDataContext)
  const { alternativeSearchEnabled } = useFeatureFlags()
  const searchStringIsEmpty = useMemo(() => !filters.searchString?.length, [filters.searchString])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const transformerBuilder = () => {
    if (alternativeSearchEnabled) {
      return alternativeTransformAutocompletePractices
    }
    return transformAutocompletePractices
  }

  const isAlternativeSearchTypesIncluded = useMemo(
    () => filters?.searchType && newSearchTypesPractices.includes(filters?.searchType),
    [filters?.searchType]
  )

  const transformerFunctionForPractice = transformerBuilder()

  const transformKeywords = useCallback(
    (keywords: AutocompleteSpecialty[]) =>
      keywords
        .map((keyword) => transformAutocompleteKeyword(keyword, tenantSettings?.locales[0] as string))
        .map((keyword) => ({
          ...keyword,
          onSelect: () => {
            changeFilter('selectedKeywordName', keyword.name)
            changeFilter('selectedKeywordSlug', keyword.slug)
            changeFilter('selectedKeywordId', keyword.id)
            changeSearch(keyword.name)
            setIsSearchFieldFocused(false)
          }
        })),
    [changeFilter, changeSearch, setIsSearchFieldFocused, tenantSettings?.locales]
  )

  const matchingKeywords = useMemo(() => {
    const maxItems = 4

    if (searchStringIsEmpty) {
      return []
    }

    const specialtiesCount = specialties.length
    const conditionsCount = conditionsProcedures.length

    let specialtiesItems: typeof specialties
    let conditionsItems: typeof conditionsProcedures

    if (specialtiesCount >= 2 && conditionsCount >= 2) {
      specialtiesItems = specialties.slice(0, 2)
      conditionsItems = conditionsProcedures.slice(0, 2)
    } else {
      const remainingForSpecialties = Math.min(specialtiesCount, maxItems)
      specialtiesItems = specialties.slice(0, remainingForSpecialties)

      const remainingSlots = maxItems - specialtiesItems.length
      conditionsItems = conditionsProcedures.slice(0, remainingSlots)
    }

    const combined = [...specialtiesItems, ...conditionsItems]

    return transformKeywords(combined)
  }, [specialties, conditionsProcedures, searchStringIsEmpty, transformKeywords])

  const matchingSpecialties = useMemo(
    () => (searchStringIsEmpty ? [] : transformKeywords(specialties.slice(0, 2))),
    [searchStringIsEmpty, specialties, transformKeywords]
  )

  const matchingConditionsProcedures = useMemo(
    () => (searchStringIsEmpty ? [] : transformKeywords(conditionsProcedures.slice(0, 2))),
    [conditionsProcedures, searchStringIsEmpty, transformKeywords]
  )

  const specialtiesWithoutMatchesKeywords = useMemo(
    () =>
      transformKeywords(
        searchStringIsEmpty
          ? specialties
          : specialties.filter((spec) => !(isMobile ? matchingKeywords : []).some((keyword) => keyword.id === spec.id))
      ),
    [transformKeywords, searchStringIsEmpty, specialties, isMobile, matchingKeywords]
  )

  const conditionsProceduresWithoutMatchesKeywords = useMemo(
    () =>
      transformKeywords(
        searchStringIsEmpty
          ? conditionsProcedures
          : conditionsProcedures.filter(
              (spec) => !(isMobile ? matchingKeywords : []).some((keyword) => keyword.id === spec.id)
            )
      ),
    [conditionsProcedures, isMobile, matchingKeywords, searchStringIsEmpty, transformKeywords]
  )

  const profiles = (
    filters.searchType === SearchTypes.specialists || filters.searchType === SearchTypes.healthAndSocialCare
      ? specialistsAutocomplete.map((specialist) =>
          transformAutocompleteSpecialist(specialist, tenantSettings?.locales[0] as string)
        )
      : practicesAutocomplete.map((practice) =>
          transformerFunctionForPractice(practice, tenantSettings?.locales[0] as string)
        )
  ).map((profile) => ({
    ...profile,
    onSelect: () => {
      openProfilePage(profile.slug)
    }
  }))

  const isEmptyContent = useMemo(
    () => profiles.length === 0 && specialties.length === 0 && conditionsProcedures.length === 0,
    [conditionsProcedures.length, profiles.length, specialties.length]
  )

  const listBuilder = () => {
    if (isEmptyContent) {
      return (
        <EmptySearchResultsContainer>
          <NoResultsComponent onClick={onClose} />
        </EmptySearchResultsContainer>
      )
    }

    return (
      <>
        {!isAlternativeSearchTypesIncluded && (
          <>
            <SearchResultsList
              hideTitle
              matchingKeywords={matchingKeywords}
              searchString={filters.searchString}
              bubbleView
            />
            {!!(conditionsProceduresWithoutMatchesKeywords?.length || specialtiesWithoutMatchesKeywords?.length) && (
              <>
                <SearchResultsList
                  rootContainerStyles={{ padding: '16px 20px' }}
                  keywords={conditionsProceduresWithoutMatchesKeywords}
                  hideTitle={!conditionsProcedures?.length}
                  searchString={filters.searchString}
                  bubbleView
                />
                <SearchResultsList
                  rootContainerStyles={{ padding: '16px 20px' }}
                  keywords={specialtiesWithoutMatchesKeywords}
                  isSpecialtiesSearch
                  hideTitle={!specialties?.length}
                  searchString={filters.searchString}
                  bubbleView
                />
              </>
            )}
          </>
        )}
        {!!profiles?.length && (
          <SearchResultsList
            rootContainerStyles={{ padding: '16px 20px' }}
            items={profiles}
            searchString={filters.searchString}
            specialistOrPractice
            shouldShowProfiles
            handleCloseSearch={onClose}
            bubbleView
          />
        )}
      </>
    )
  }

  return (
    <SearchResultsModalRoot
      onTouchStart={onInteract}
      sx={{
        top: '110%',
        textAlign: 'start',
        display: !isOpen ? 'none' : 'block',
        ...customStyle,
        ...(!isAlternativeSearchTypesIncluded ? { left: 0, right: 0 } : {})
      }}
    >
      <Hidden lgUp implementation="css">
        <MobileListContainer>{listBuilder()}</MobileListContainer>
      </Hidden>
      <Hidden lgDown implementation="css">
        <Grid container wrap="nowrap">
          {isEmptyContent ? (
            <EmptySearchResultsContainer>
              <NoResultsComponent onClick={onClose} />
            </EmptySearchResultsContainer>
          ) : (
            <>
              {!isAlternativeSearchTypesIncluded && (
                <>
                  <SearchModalItemContainer item>
                    <SearchResultsList
                      matchingKeywords={matchingConditionsProcedures}
                      rootContainerStyles={{ padding: '16px 24px' }}
                      keywords={conditionsProceduresWithoutMatchesKeywords}
                      searchString={filters.searchString}
                      bubbleView
                    />
                  </SearchModalItemContainer>
                  <SearchModalItemContainer item>
                    <SearchResultsList
                      matchingKeywords={matchingSpecialties}
                      rootContainerStyles={{ padding: '16px 24px' }}
                      keywords={specialtiesWithoutMatchesKeywords}
                      isSpecialtiesSearch
                      searchString={filters.searchString}
                      bubbleView
                    />
                  </SearchModalItemContainer>
                </>
              )}
              <SearchModalItemContainer item>
                <SearchResultsList
                  rootContainerStyles={{ padding: '8px 24px' }}
                  items={profiles}
                  searchString={filters.searchString}
                  specialistOrPractice
                  shouldShowProfiles
                />
              </SearchModalItemContainer>
            </>
          )}
        </Grid>
      </Hidden>
    </SearchResultsModalRoot>
  )
}

export default AutocompleteResultsModal
